import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, CreditCard, Receipt, Wallet, ChevronDown, AlertCircle } from 'lucide-react';
import { supabase } from '../lib/supabaseClient';

interface ApplyProductSalePaymentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  productSale: any; // The product sale to apply payment to
}

const paymentMethods = [
  {
    id: 'credit_card',
    name: 'Record Credit / Debit',
    icon: <CreditCard className="h-5 w-5 text-purple-500" />,
    description: 'Record credit or debit card payment'
  },
  {
    id: 'bank_transfer',
    name: 'Record Bank Transfer',
    icon: <Receipt className="h-5 w-5 text-green-500" />,
    description: 'Record direct bank transfer payment'
  },
  {
    id: 'cash',
    name: 'Record Cash',
    icon: <Wallet className="h-5 w-5 text-yellow-500" />,
    description: 'Record cash payment'
  }
];

export default function ApplyProductSalePaymentModal({
  isOpen,
  onClose,
  onSuccess,
  productSale
}: ApplyProductSalePaymentModalProps) {
  const [isAnimating, setIsAnimating] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedMethod, setSelectedMethod] = useState(paymentMethods[0]);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [paymentAmount, setPaymentAmount] = useState('');
  const [reference, setReference] = useState('');
  const [notes, setNotes] = useState('');

  // Calculate payment details
  const totalAmount = productSale?.total_amount || 0;
  const existingPayments = productSale?.payments || [];
  const totalPaid = existingPayments.reduce((sum: number, payment: any) => 
    payment.status === 'completed' ? sum + (payment.base_amount || payment.amount) : sum, 0
  );
  const remainingBalance = Math.max(0, totalAmount - totalPaid);

  useEffect(() => {
    if (isOpen) {
      setIsAnimating(true);
      // Set default payment amount to remaining balance
      setPaymentAmount(remainingBalance.toFixed(2));
    }
  }, [isOpen, remainingBalance]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = () => {
      setIsDropdownOpen(false);
    };

    if (isDropdownOpen) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [isDropdownOpen]);

  const handleTransitionEnd = () => {
    if (!isOpen) {
      setIsAnimating(false);
      setError(null);
      setSelectedMethod(paymentMethods[0]);
      setIsDropdownOpen(false);
      setPaymentAmount('');
      setReference('');
      setNotes('');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!productSale?.id) return;

    const amount = parseFloat(paymentAmount);
    if (isNaN(amount) || amount <= 0) {
      setError('Please enter a valid payment amount');
      return;
    }

    if (amount > remainingBalance) {
      setError('Payment amount cannot exceed remaining balance');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Create payment record
      const { data: payment, error: paymentError } = await supabase
        .from('payments')
        .insert({
          business_id: productSale.business_id,
          product_sale_id: productSale.id,
          booking_id: null, // Product sales don't have booking_id
          amount: amount,
          method: selectedMethod.id,
          status: 'completed',
          reference: reference || null,
          notes: notes || `Payment for product sale #${productSale.sale_number}`,
          created_at: new Date().toISOString()
        })
        .select()
        .single();

      if (paymentError) throw paymentError;

      // Calculate new payment status
      const newTotalPaid = totalPaid + amount;
      const newPaymentStatus = newTotalPaid >= totalAmount ? 'paid' : 'partial';

      // Update product sale payment status
      const { error: updateError } = await supabase
        .from('product_sales')
        .update({
          payment_status: newPaymentStatus,
          updated_at: new Date().toISOString()
        })
        .eq('id', productSale.id);

      if (updateError) throw updateError;

      console.log('✅ Payment applied successfully:', payment);
      onSuccess();
      onClose();
    } catch (error) {
      console.error('❌ Failed to apply payment:', error);
      setError(error instanceof Error ? error.message : 'Failed to apply payment');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen && !isAnimating) return null;

  const modalContent = (
    <div 
      className={`fixed inset-0 bg-black transition-opacity duration-300 ${
        isOpen ? 'bg-opacity-50' : 'bg-opacity-0'
      } z-[100030]`}
      onClick={onClose}
    >
      <div 
        className={`fixed inset-y-0 right-0 w-full md:w-[600px] bg-white transform transition-transform duration-300 ease-out shadow-2xl ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
        onTransitionEnd={handleTransitionEnd}
        onClick={e => e.stopPropagation()}
      >
        <div className="h-full flex flex-col">
          {/* Header - Clean Modern Design */}
          <div className="bg-gray-900 border-b border-gray-800">
            <div className="flex items-center justify-between px-6 py-4">
              <div className="flex items-center space-x-3">
                <button
                  onClick={onClose}
                  className="text-gray-400 hover:text-white transition-colors"
                >
                  <X className="h-6 w-6" />
                </button>
                <h2 className="text-xl font-semibold text-white">
                  Record Payment
                </h2>
              </div>
            </div>
          </div>

          <form 
            onSubmit={handleSubmit} 
            className="flex-1 overflow-y-auto bg-gray-50"
            style={{ WebkitOverflowScrolling: 'touch' }}
          >
            <div className="space-y-0">
              {error && (
                <div className="bg-red-50 border-b border-red-200 text-red-700 px-6 py-4 text-sm">
                  <div className="flex items-center">
                    <AlertCircle className="h-4 w-4 mr-2 flex-shrink-0" />
                    {error}
                  </div>
                </div>
              )}

              {/* Sale Summary */}
              <div className="bg-white border-b border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Sale Summary</h3>
                
                {/* Show items from the product sale */}
                <div className="space-y-2 mb-4">
                  {productSale?.items?.map((item: any, index: number) => (
                    <div key={index} className="flex justify-between items-center text-sm">
                      <span className="text-gray-700">
                        {item.product_name} × {item.quantity}
                      </span>
                      <span className="font-medium text-gray-900">
                        ${(item.unit_price * item.quantity).toFixed(2)}
                      </span>
                    </div>
                  ))}
                </div>
                
                <div className="border-t border-gray-200 pt-3 space-y-2">
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-gray-600">Subtotal</span>
                    <span className="font-medium text-gray-900">
                      ${productSale?.subtotal_amount?.toFixed(2) || '0.00'}
                    </span>
                  </div>
                  {productSale?.discount_amount > 0 && (
                    <div className="flex justify-between items-center text-sm">
                      <span className="text-green-600">Discount</span>
                      <span className="font-medium text-green-600">
                        -${productSale.discount_amount.toFixed(2)}
                      </span>
                    </div>
                  )}
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-gray-600">GST ({productSale?.tax_rate || 5}%)</span>
                    <span className="font-medium text-gray-900">
                      ${productSale?.tax_amount?.toFixed(2) || '0.00'}
                    </span>
                  </div>
                  <div className="border-t border-gray-200 pt-2 flex justify-between items-center">
                    <span className="text-lg font-semibold text-gray-900">Total Amount</span>
                    <span className="text-lg font-bold text-green-600">
                      ${totalAmount.toFixed(2)}
                    </span>
                  </div>
                  {totalPaid > 0 && (
                    <div className="flex justify-between items-center text-sm">
                      <span className="text-gray-600">Amount Paid</span>
                      <span className="font-medium text-green-600">
                        ${totalPaid.toFixed(2)}
                      </span>
                    </div>
                  )}
                  <div className="flex justify-between items-center">
                    <span className="text-lg font-semibold text-gray-900">Amount Due</span>
                    <span className="text-lg font-bold text-red-600">
                      ${remainingBalance.toFixed(2)}
                    </span>
                  </div>
                </div>
              </div>

              {/* Payment Method */}
              <div className="bg-white border-b border-gray-200 p-6">
                <label className="block text-lg font-semibold text-gray-900 mb-4">
                  Payment Method
                </label>
                <div className="relative">
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setIsDropdownOpen(!isDropdownOpen);
                    }}
                    className="w-full flex items-center p-4 border-2 border-blue-500 bg-blue-50 rounded-lg transition-colors"
                  >
                    <div className="h-12 w-12 rounded-lg flex items-center justify-center mr-4 bg-blue-100">
                      {selectedMethod.icon}
                    </div>
                    <div className="flex-1 text-left">
                      <div className="text-sm font-semibold text-gray-900">{selectedMethod.name}</div>
                      <div className="text-sm text-gray-600">{selectedMethod.description}</div>
                    </div>
                    <ChevronDown className={`h-5 w-5 text-gray-400 transition-transform ${
                      isDropdownOpen ? 'rotate-180' : ''
                    }`} />
                  </button>

                  {/* Dropdown Menu */}
                  {isDropdownOpen && (
                    <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-10">
                      {paymentMethods.map((method, index) => (
                        <button
                          key={method.id}
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedMethod(method);
                            setIsDropdownOpen(false);
                          }}
                          className={`w-full flex items-center p-3 hover:bg-gray-50 transition-colors ${
                            selectedMethod.id === method.id ? 'bg-blue-50' : ''
                          } ${index === 0 ? 'rounded-t-lg' : ''} ${
                            index === paymentMethods.length - 1 ? 'rounded-b-lg' : ''
                          }`}
                        >
                          <div className="h-8 w-8 rounded-lg flex items-center justify-center mr-3 bg-gray-100">
                            {method.icon}
                          </div>
                          <div className="flex-1 text-left">
                            <div className="text-sm font-medium text-gray-900">{method.name}</div>
                            <div className="text-xs text-gray-600">{method.description}</div>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Payment Details */}
              <div className="bg-white p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Payment Details</h3>

                <div className="space-y-4">
                  <div>
                    <label className="block text-xs font-bold text-gray-700 mb-2">
                      Amount *
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <span className="text-gray-500 text-sm font-medium">$</span>
                      </div>
                      <input
                        type="number"
                        step="0.01"
                        min="0.01"
                        max={remainingBalance}
                        value={paymentAmount}
                        onChange={(e) => setPaymentAmount(e.target.value)}
                        className="w-full pl-8 pr-4 py-3 text-base bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                        placeholder="0.00"
                        required
                        style={{ 
                          minHeight: '48px',
                          fontSize: '16px'
                        }}
                      />
                    </div>
                    <div className="mt-2">
                      <p className="text-xs text-gray-600">
                        Maximum amount: ${remainingBalance.toFixed(2)}
                      </p>
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-gray-700 mb-2">
                      Reference Number
                    </label>
                    <input
                      type="text"
                      value={reference}
                      onChange={(e) => setReference(e.target.value)}
                      className="w-full px-4 py-3 text-base bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                      placeholder="Enter reference number (optional)"
                      style={{ 
                        minHeight: '48px',
                        fontSize: '16px'
                      }}
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-gray-700 mb-2">
                      Notes
                    </label>
                    <textarea
                      rows={3}
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      className="w-full px-4 py-3 text-base bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors resize-none"
                      placeholder="Add any additional notes about this payment..."
                      style={{ fontSize: '16px' }}
                    />
                  </div>

                  {/* Submit Button */}
                  <button
                    type="submit"
                    disabled={loading || !paymentAmount || parseFloat(paymentAmount) <= 0}
                    className={`w-full px-6 py-4 text-base font-bold text-white rounded-lg transition-colors ${
                      (!loading && paymentAmount && parseFloat(paymentAmount) > 0)
                        ? 'bg-blue-600 hover:bg-blue-700' 
                        : 'bg-gray-300 cursor-not-allowed'
                    } disabled:opacity-50 disabled:cursor-not-allowed`}
                    style={{ minHeight: '52px' }}
                  >
                    {loading ? (
                      <div className="flex items-center justify-center">
                        <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin mr-3"></div>
                        Processing Payment...
                      </div>
                    ) : (
                      `Record Payment`
                    )}
                  </button>
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
}
